//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React from 'react';
import { RegisterOptions, useFormContext } from 'react-hook-form';
import styled, { css } from 'styled-components';

import { UploadTab } from '@campaigns/components/shared/product-details-modal/variants/image-preview-popover/tabs/UploadTab';
import { UrlTab } from '@campaigns/components/shared/product-details-modal/variants/image-preview-popover/tabs/UrlTab';
import { SAMPLING_CAMPAIGNS_TRACKING_ID } from '@campaigns/constants/sampling/SamplingCampaignsTrackingId';
import { useProductModal } from '@campaigns/contexts/ProductModalContext';
import { EditableBVSamplingProduct } from '@campaigns/types/sampling/SamplingProductTypes';
import { PrimaryLinkButton } from '@components/atoms/button/Button';
import { CaptionText } from '@components/atoms/text/Text';
import { Tabs } from '@components/primitives/tabs/Tabs';
import { SUPPORTED_IMAGE_MIME_TYPES } from '@utils/mimeTypes';
import { toGrammaticallyCorrectList } from '@utils/stringUtils';

export interface VariantImageUploadPopoverProps {
  index: number;
  onClosePopover: () => void;
  onClearImage: () => void;
  onFileSelect: (file: File | undefined) => void;
  onPreview: () => void;
  rules: RegisterOptions | undefined;
}

export const VariantImageUploadPopover = ({
  index,
  onClearImage,
  onClosePopover,
  onFileSelect,
  onPreview,
  rules,
}: VariantImageUploadPopoverProps) => {
  const { mainProductImage } = useProductModal();
  const { getValues, setValue } = useFormContext<EditableBVSamplingProduct>();

  const TABS = [
    {
      content: (
        <UrlTab
          index={index}
          onClosePopover={onClosePopover}
          onPreview={onPreview}
          rules={rules}
        />
      ),
      title: 'URL',
      value: 'url',
    },
    {
      content: (
        <UploadTab
          index={index}
          onClearImage={onClearImage}
          onClosePopover={onClosePopover}
          onFileSelect={onFileSelect}
          rules={rules}
        />
      ),
      title: 'Upload',
      value: 'upload',
    },
  ];

  const handleUseMainProductImage = () => {
    const currentMainImgUrl = getValues('imgUrl');

    // If we have a current main image URL, use that
    if (currentMainImgUrl) {
      if (currentMainImgUrl.startsWith('blob:') && mainProductImage?.file) {
        // Use the main product image file for this variant
        onFileSelect(mainProductImage.file);
        onClosePopover();
      } else {
        // For regular URLs
        setValue(`variations.${index}.imgUrl`, currentMainImgUrl);
        onFileSelect(undefined);
        onClosePopover();
        onPreview();
      }
    }
  };

  return (
    <PopoverContent>
      <StyledPrimaryLinkButton
        onClick={handleUseMainProductImage}
        trackingId={
          SAMPLING_CAMPAIGNS_TRACKING_ID.PRODUCT_MODAL_ADD_VARIANT_IMAGE_USE_MAIN_PRODUCT_IMAGE_BUTTON
        }
      >
        Use the same image as the main product
      </StyledPrimaryLinkButton>
      <CaptionWrapper>
        <CaptionText>File types supported:</CaptionText>
        <CaptionText>
          {toGrammaticallyCorrectList(SUPPORTED_IMAGE_MIME_TYPES)}
        </CaptionText>
      </CaptionWrapper>

      <StyledTabs defaultValue='url'>
        <Tabs.List>
          {TABS.map((tab) => (
            <Tabs.Trigger
              key={tab.value}
              trackingId={
                SAMPLING_CAMPAIGNS_TRACKING_ID.PRODUCT_MODAL_ADD_VARIANT_IMAGE_URL_TAB_TRIGGER
              }
              value={tab.value}
            >
              {tab.title}
            </Tabs.Trigger>
          ))}
        </Tabs.List>
        {TABS.map((tab) => (
          <StyledTabContent key={tab.value} value={tab.value}>
            {tab.content}
          </StyledTabContent>
        ))}
      </StyledTabs>
    </PopoverContent>
  );
};

const PopoverContent = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacings[2]};
    padding: ${theme.spacings[3]};
    width: ${theme.mediumMeasure};
    height: calc(${theme.spacings[1]} * 80); // 320px
  `}
`;

const StyledPrimaryLinkButton = styled(PrimaryLinkButton)`
  align-self: flex-start;
`;

const StyledTabs = styled(Tabs)`
  box-shadow: none;
`;

const CaptionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacings[1]};
`;

const StyledTabContent = styled(Tabs.Content)`
  padding-top: ${({ theme }) => theme.spacings[3]};
`;
